// Utility functions for day-based filtering of open houses

/**
 * Generate day filter options based on open house data
 * @param {Array} openHouses - Array of open house objects
 * @returns {Array} Array of filter options with label and value
 */
export const generateDayFilterOptions = (openHouses) => {
  if (!openHouses || openHouses.length === 0) {
    return [{ label: 'All Days', value: 'all' }];
  }

  // Extract unique days from open house start times
  const daysWithOpenHouses = new Set();
  
  openHouses.forEach(openHouse => {
    if (openHouse.openHouseStartTime) {
      const date = new Date(openHouse.openHouseStartTime);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
      daysWithOpenHouses.add(dayOfWeek);
    }
  });

  // Convert day numbers to readable names (in chronological order)
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Create options array starting with "All Days"
  const options = [{ label: 'All Days', value: 'all' }];
  
  // Find the earliest date from the open houses to determine starting day
  let earliestDate = null;
  openHouses.forEach(openHouse => {
    if (openHouse.openHouseStartTime) {
      const date = new Date(openHouse.openHouseStartTime);
      if (!earliestDate || date < earliestDate) {
        earliestDate = date;
      }
    }
  });
  
  // Get the day of week from the earliest date
  const startDay = earliestDate ? earliestDate.getDay() : 0;
  
  // Build sorted days array starting from the earliest day
  const sortedDays = [];
  
  // Add days from startDay to end of week (Saturday = 6)
  for (let day = startDay; day <= 6; day++) {
    if (daysWithOpenHouses.has(day)) {
      sortedDays.push(day);
    }
  }
  
  // Add days from beginning of week (Sunday = 0) to before startDay
  for (let day = 0; day < startDay; day++) {
    if (daysWithOpenHouses.has(day)) {
      sortedDays.push(day);
    }
  }
  
  // Add sorted days to options
  sortedDays.forEach(dayNumber => {
    options.push({
      label: dayNames[dayNumber],
      value: dayNumber.toString()
    });
  });

  return options;
};

/**
 * Filter open houses by selected day
 * @param {Array} openHouses - Array of open house objects
 * @param {string} selectedDay - Selected day value ('all' or day number as string)
 * @returns {Array} Filtered array of open houses
 */
export const filterOpenHousesByDay = (openHouses, selectedDay) => {
  if (!openHouses || openHouses.length === 0) {
    return [];
  }

  if (selectedDay === 'all') {
    return openHouses;
  }

  const dayNumber = parseInt(selectedDay);
  
  return openHouses.filter(openHouse => {
    if (!openHouse.openHouseStartTime) {
      return false;
    }
    
    const date = new Date(openHouse.openHouseStartTime);
    return date.getDay() === dayNumber;
  });
};

/**
 * Get the count of open houses for a specific day
 * @param {Array} openHouses - Array of open house objects
 * @param {string} dayValue - Day value ('all' or day number as string)
 * @returns {number} Count of open houses for that day
 */
export const getOpenHouseCountForDay = (openHouses, dayValue) => {
  if (dayValue === 'all') {
    return openHouses ? openHouses.length : 0;
  }
  
  return filterOpenHousesByDay(openHouses, dayValue).length;
};
